import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  UserPlus, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  User,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMembershipRequestsPage() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/admin/membership-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Fehler beim Laden der Anträge');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/admin/membership-requests/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Antrag genehmigt! Mitglied wurde erstellt und benachrichtigt.');
      fetchRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Genehmigen';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId, reason = '') => {
    const rejectReason = reason || prompt('Grund für Ablehnung (optional):');
    
    setProcessing(requestId);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/admin/membership-requests/${requestId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Antrag abgelehnt. Antragsteller wurde benachrichtigt.');
      fetchRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Ablehnen';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs uppercase tracking-wider">
            <Clock size={12} />
            Ausstehend
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs uppercase tracking-wider">
            <CheckCircle size={12} />
            Genehmigt
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-500 text-xs uppercase tracking-wider">
            <XCircle size={12} />
            Abgelehnt
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    return req.status === filter;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
              <UserPlus className="text-primary" />
              Mitgliedsanträge
            </h1>
            <p className="text-muted-foreground mt-1">
              {pendingCount > 0 ? (
                <span className="text-yellow-500">{pendingCount} ausstehende Anträge</span>
              ) : (
                'Keine ausstehenden Anträge'
              )}
            </p>
          </div>
          
          <Button 
            onClick={fetchRequests} 
            variant="outline" 
            className="rounded-none"
            data-testid="refresh-requests-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { value: 'pending', label: 'Ausstehend', count: requests.filter(r => r.status === 'pending').length },
            { value: 'approved', label: 'Genehmigt', count: requests.filter(r => r.status === 'approved').length },
            { value: 'rejected', label: 'Abgelehnt', count: requests.filter(r => r.status === 'rejected').length },
            { value: 'all', label: 'Alle', count: requests.length },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 text-sm uppercase tracking-wider border transition-colors ${
                filter === tab.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid={`filter-${tab.value}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* No Requests */}
        {filteredRequests.length === 0 && (
          <div className="text-center py-12 bg-card border border-border">
            <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {filter === 'pending' ? 'Keine ausstehenden Anträge' : 'Keine Anträge gefunden'}
            </p>
          </div>
        )}

        {/* Requests List */}
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div 
              key={request.id}
              className={`bg-card border overflow-hidden ${
                request.status === 'pending' ? 'border-yellow-500/50' : 'border-border'
              }`}
              data-testid={`request-${request.id}`}
            >
              {/* Header Row */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/20"
                onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {request.first_name} {request.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{request.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(request.status)}
                  <span className="text-xs text-muted-foreground hidden sm:block">
                    {formatDate(request.submitted_at)}
                  </span>
                  {expandedId === request.id ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === request.id && (
                <div className="border-t border-border p-4 bg-muted/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Contact Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-primary" />
                        <a href={`mailto:${request.email}`} className="hover:text-primary">
                          {request.email}
                        </a>
                      </div>
                      {request.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-primary" />
                          <a href={`tel:${request.phone}`} className="hover:text-primary">
                            {request.phone}
                          </a>
                        </div>
                      )}
                      {(request.street || request.city) && (
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-primary mt-0.5" />
                          <div>
                            {request.street && <p>{request.street}</p>}
                            {request.zip && request.city && <p>{request.zip} {request.city}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Additional Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span>Eingereicht: {formatDate(request.submitted_at)}</span>
                      </div>
                      {request.processed_at && (
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          <span>Bearbeitet: {formatDate(request.processed_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  {request.message && (
                    <div className="mb-4 p-3 bg-background border border-border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <FileText className="w-4 h-4" />
                        Nachricht
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{request.message}</p>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {request.status === 'rejected' && request.rejection_reason && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30">
                      <p className="text-sm text-red-500">
                        <strong>Ablehnungsgrund:</strong> {request.rejection_reason}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  {request.status === 'pending' && (
                    <div className="flex gap-3 pt-2 border-t border-border">
                      <Button
                        onClick={() => handleApprove(request.id)}
                        disabled={processing === request.id}
                        className="bg-green-600 hover:bg-green-700 rounded-none uppercase tracking-wider"
                        data-testid={`approve-${request.id}`}
                      >
                        {processing === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Genehmigen
                      </Button>
                      <Button
                        onClick={() => handleReject(request.id)}
                        disabled={processing === request.id}
                        variant="destructive"
                        className="rounded-none uppercase tracking-wider"
                        data-testid={`reject-${request.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Ablehnen
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
